import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineDateRange } from 'react-icons/md';
import { Stack, Typography } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import Row from 'src/components/dashboard/DashboardPage/widgets/OrgUnitResponseRateWidget/Row';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import NoResultsToShowWarning from 'src/components/followUp/common/warning/NoResultsToShowWarning';
import StaticGraphWarning from 'src/components/followUp/common/warning/StaticGraphWarning';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import type { OrgUnitData } from './constants';
import { useExportOptions } from './hooks';

const OrgUnitResponseRateWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation(['common', 'followUp']);
  const ariaId = useId();

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.ORG_UNIT_RESPONSE_RATE,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, queryFilters } =
    useWidgetQuery<StackedNumbersList>(endpoint, { noFilter: true });

  const [rootOrgUnit, ...childOrgUnits] = useMemo<
    Array<
      | OrgUnitData
      // Cover the case when data hasn't loaded yet and the source array may be empty.
      | undefined
    >
  >(
    () =>
      data?.results.map((orgUnit) => {
        const invitees =
          orgUnit.stacks.find((stack) => stack.stackLabel === 'invitees')
            ?.value ?? 0;
        const respondents =
          orgUnit.stacks.find((stack) => stack.stackLabel === 'respondents')
            ?.value ?? 0;

        return {
          label: orgUnit.label,
          invitees,
          respondentPercentage: getRelativeValue(respondents, invitees, 0),
          respondents,
        };
      }) ?? [],
    [data],
  );

  const exportOptions = useExportOptions({
    rootOrgUnit,
    childOrgUnits,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      notAffectedBy="filters"
      minHeight="auto"
      ariaId={ariaId}
      refetch={refetch}
      loading={isFetching}
      error={errorMsg}
      exportOptions={exportOptions}
      header={
        <WidgetHeaderWithPill
          title={config.name}
          pillProps={{
            label: t('common:toDate'),
            icon: <MdOutlineDateRange />,
            color: 'secondary',
          }}
          ariaId={ariaId}
        />
      }
      footer={
        <Stack
          sx={{
            height: '100%',
            justifyContent: 'center',
            alignItems: 'flex-start',
            px: 3,
          }}
        >
          {data?.totalObjects === 0 ? (
            <NoResultsToShowWarning />
          ) : (
            <StaticGraphWarning />
          )}
        </Stack>
      }
    >
      {data && rootOrgUnit && (
        <Stack
          sx={{
            p: 3,
            width: '100%',
            height: '100%',
            gap: 3,
            overflow: 'scroll',
          }}
        >
          <Row
            label={rootOrgUnit.label}
            variant="violet"
            totalValue={rootOrgUnit.invitees}
            relativeValue={rootOrgUnit.respondentPercentage}
            absoluteValue={rootOrgUnit.respondents}
          />
          <Typography
            variant="body3"
            sx={{
              fontWeight: '600',
            }}
          >
            {t('followUp:statistics.orgUnitDividedIntoItsSubunits', {
              orgUnit: rootOrgUnit.label,
            })}
          </Typography>

          {childOrgUnits.map(
            (childOrgUnit) =>
              childOrgUnit && (
                <Row
                  key={childOrgUnit.label}
                  label={childOrgUnit.label}
                  variant="lightViolet"
                  totalValue={childOrgUnit.invitees}
                  relativeValue={childOrgUnit.respondentPercentage}
                  absoluteValue={childOrgUnit.respondents}
                />
              ),
          )}
        </Stack>
      )}
    </WidgetFrame>
  );
};

export default OrgUnitResponseRateWidget;
